import { ApiError, createApiClient } from "@commerce-os/api-client";
import { createI18n, isLocale, type Locale } from "@commerce-os/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type RouterHistory, RouterProvider } from "@tanstack/react-router";
import { I18nextProvider } from "react-i18next";

import { type AppContext, AppContextProvider } from "./context";
import { readDevice } from "./device-storage";
import { createRealtime, noRealtime } from "./realtime";
import { createAppRouter } from "./router";
import { createProductSearch } from "./search";
import { createSession } from "./session";
import { createUploader } from "./upload";

export interface AdminAppOptions {
  /** API origin; empty means same origin (the Vite proxy in development). */
  baseUrl?: string;
  fetch?: typeof fetch;
  history?: RouterHistory;
  validateResponses?: boolean;
  lng?: Locale;
  /** How often to check an uploaded image's processing (tests use a few ms). */
  uploadPollMs?: number;
  /** Connect to the API's realtime signals (default true; component tests turn it off). */
  realtime?: boolean;
}

/** Builds the whole Admin (i18n, API client, query cache, router). `main.tsx` and the tests both use it. */
export async function createAdminApp(options: AdminAppOptions = {}) {
  const stored = readDevice("locale");
  const i18n = await createI18n({
    lng: options.lng ?? (stored !== undefined && isLocale(stored) ? stored : "en"),
  });
  const syncLang = () => {
    document.documentElement.lang = i18n.language;
  };
  syncLang();
  i18n.on("languageChanged", syncLang);

  const session = createSession();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // 4xx answers are final; retry only network and server failures.
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
        refetchOnWindowFocus: false,
      },
    },
  });
  let onSignedOut: () => void = () => undefined;
  const api = createApiClient({
    baseUrl: options.baseUrl ?? "",
    getAccessToken: () => session.token(),
    onRefreshed: (result) => {
      session.setToken(result.accessToken);
    },
    onSignedOut: () => {
      onSignedOut();
    },
    validateResponses: options.validateResponses ?? false,
    ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
  });
  const fetchFn = options.fetch ?? globalThis.fetch.bind(globalThis);
  const uploadFile = createUploader(api, fetchFn, options.uploadPollMs);
  const searchProducts = createProductSearch(fetchFn);
  const realtime = options.realtime === false ? noRealtime : createRealtime(options.baseUrl ?? "", session);
  const context: AppContext = { api, queryClient, session, i18n, uploadFile, searchProducts, realtime };
  const router = createAppRouter(context, options.history);
  onSignedOut = () => {
    // A dead session (expired, revoked): forget everything and go to sign-in, unless already signed out.
    const wasSignedIn = session.token() !== undefined;
    session.clear();
    queryClient.clear();
    realtime.close();
    if (wasSignedIn) void router.navigate({ to: "/sign-in" });
  };

  function App() {
    return (
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AppContextProvider value={context}>
            <RouterProvider router={router} />
          </AppContextProvider>
        </QueryClientProvider>
      </I18nextProvider>
    );
  }

  return { App, router, context };
}
